import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import { useTheme } from '../../theme'

/**
 * Main navigation component with user menu and navigation items
 * Matches the Angular frontend design with glass effect header
 */
export const Navigation: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { user, signOut } = useAuth()
  const permissions = usePermissions()
  const { branding } = useTheme()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false)
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isUserMenuOpen])

  const handleSignOut = async () => {
    try {
      setIsUserMenuOpen(false) // Close dropdown first
      await signOut()
      // Use setTimeout to avoid act() warnings in tests
      setTimeout(() => {
        navigate('/login')
      }, 0)
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen)
  }

  const getUserAvatar = () => {
    const name = user?.nickname || user?.name || 'Usuario'
    return name.charAt(0).toUpperCase()
  }

  return (
    <div className="glass sticky top-0 z-40 border-b border-white/20">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl flex items-center justify-center shadow-soft">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-secondary-900 to-secondary-700 bg-clip-text text-transparent">
                  {branding.organizationName}
                </h1>
              </div>
            </div>

            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => navigate('/dashboard')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/dashboard'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/50'
                }`}
              >
                Dashboard
              </button>

              {/* Admin-only navigation items */}
              {permissions?.canManageUsers() && (
                <button
                  onClick={() => navigate('/users')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === '/users'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/50'
                  }`}
                >
                  Usuarios
                </button>
              )}

              {permissions?.canManageRoles() && (
                <button
                  onClick={() => navigate('/roles')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === '/roles'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/50'
                  }`}
                >
                  Roles
                </button>
              )}

              {permissions?.canManageUsers() && (
                <button
                  onClick={() => navigate('/workflows')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === '/workflows'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/50'
                  }`}
                >
                  Flujos
                </button>
              )}

              {permissions?.canManageUsers() && (
                <button
                  onClick={() => navigate('/job-definitions')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname.startsWith('/job-definitions')
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/50'
                  }`}
                >
                  Definiciones
                </button>
              )}

              {permissions?.canManageUsers() && (
                <button
                  onClick={() => navigate('/integrations')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === '/integrations'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-white/50'
                  }`}
                >
                  Integraciones
                </button>
              )}
            </nav>
          </div>

          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-white/50 transition-all duration-200"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <svg
                className="w-6 h-6 text-secondary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <div className="relative" ref={dropdownRef}>
              <button
                className="flex items-center space-x-3 p-2 rounded-xl hover:bg-white/50 transition-all duration-200"
                onClick={toggleUserMenu}
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl flex items-center justify-center font-semibold shadow-soft">
                  {getUserAvatar()}
                </div>
                <svg
                  className={`w-4 h-4 text-secondary-600 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 glass rounded-2xl shadow-strong border border-white/20 animate-slide-up z-50">
                  <div className="p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl flex items-center justify-center font-semibold">
                        {getUserAvatar()}
                      </div>
                      <div>
                        <p className="font-semibold text-secondary-900">
                          {user?.nickname || user?.name || 'Usuario'}
                        </p>
                        <p className="text-xs text-secondary-500">
                          {user?.email || 'usuario@ejemplo.com'}
                        </p>
                      </div>
                    </div>

                    {/* Mobile Navigation Items */}
                    <div className="md:hidden mb-3">
                      <hr className="border-secondary-200 mb-3" />
                      <div className="space-y-1">
                        <button
                          className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200"
                          onClick={() => {
                            navigate('/dashboard')
                            setIsUserMenuOpen(false)
                          }}
                        >
                          Dashboard
                        </button>
                        <button
                          className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200"
                          onClick={() => {
                            navigate('/new-requirement')
                            setIsUserMenuOpen(false)
                          }}
                        >
                          Nueva Solicitud
                        </button>
                        {permissions?.canManageUsers() && (
                          <button
                            className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200"
                            onClick={() => {
                              navigate('/users')
                              setIsUserMenuOpen(false)
                            }}
                          >
                            Usuarios
                          </button>
                        )}
                        {permissions?.canManageRoles() && (
                          <button
                            className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200"
                            onClick={() => {
                              navigate('/roles')
                              setIsUserMenuOpen(false)
                            }}
                          >
                            Roles
                          </button>
                        )}
                        {permissions?.canManageUsers() && (
                          <button
                            className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200"
                            onClick={() => {
                              navigate('/workflows')
                              setIsUserMenuOpen(false)
                            }}
                          >
                            Flujos
                          </button>
                        )}
                        {permissions?.canCreateSolicitudes() && (
                          <button
                            className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200"
                            onClick={() => {
                              navigate('/job-definitions')
                              setIsUserMenuOpen(false)
                            }}
                          >
                            Definiciones
                          </button>
                        )}
                        {permissions?.canManageUsers() && (
                          <button
                            className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-all duration-200"
                            onClick={() => {
                              navigate('/integrations')
                              setIsUserMenuOpen(false)
                            }}
                          >
                            Integraciones
                          </button>
                        )}
                      </div>
                    </div>

                    <hr className="border-secondary-200 my-3" />
                    <button
                      className="w-full text-left px-3 py-2 text-sm text-secondary-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200 flex items-center space-x-2"
                      onClick={handleSignOut}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Cerrar sesión</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
